'use client';

import React, { useState, useCallback, memo, useEffect } from 'react';
import { MediaAsset, MediaAssetType } from '@/types/media';
import { Product } from '@/types/product';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { MediaModalSectionName } from './common/commonTypes';
import MediaModalContent from '@/features/media-modal/components/MediaModalContent';

type Props = {
  allowedSections?: MediaModalSectionName[];
  mediaAssetType?: MediaAssetType;
  selectedProductDetails?: Product | null;
  videoScript?: string | null;
  addMedia: (media: MediaAsset[]) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialSection?: MediaModalSectionName;
  isOnlyImages?: boolean;
  multiSelection?: boolean;
  selectedMedia?: MediaAsset[];
};

export const ADD_MEDIA_MODAL_ID = 'add-library-media-modal';

const AddMediaModal = ({
  allowedSections,
  selectedProductDetails,
  addMedia,
  isOpen,
  setIsOpen,
  mediaAssetType,
  initialSection,
  isOnlyImages,
  multiSelection = true,
  selectedMedia: initialSelectedMedia,
}: Props) => {
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset[]>(initialSelectedMedia || []);
  useEffect(() => {
    setSelectedMedia(initialSelectedMedia || []);
  }, [initialSelectedMedia]);

  const onDialogClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleAdd = useCallback(() => {
    addMedia(selectedMedia);
    onDialogClose();
  }, [addMedia, selectedMedia, onDialogClose]);

  /**
   * Skipping selected images set. As the old selectedImages reference is still in closure.
   * Doesn't make sense to wait for rerender, just adding uploaded image and closing a dialog.
   */
  const setSelectedImagesAndClose = useCallback(
    (images: MediaAsset[]) => {
      addMedia(images);
      setIsOpen(false);
    },
    [addMedia, setIsOpen]
  );

  return (
    <Modal id={ADD_MEDIA_MODAL_ID} variant="large" open={isOpen} onHide={onDialogClose}>
      <TitleBar title="Add Media from Library">
        <button variant="primary" disabled={!selectedMedia.length} onClick={handleAdd}>
          Add
        </button>
      </TitleBar>
      <MediaModalContent
        initialSection={initialSection}
        product={selectedProductDetails || undefined}
        selectedMedia={selectedMedia}
        setSelectedMedia={setSelectedMedia}
        setSelectedImagesAndClose={setSelectedImagesAndClose}
        mediaAssetType={mediaAssetType}
        allowedSections={allowedSections}
        isOnlyImages={isOnlyImages}
        multiSelection={multiSelection}
      />
    </Modal>
  );
};

export default memo(AddMediaModal);
